import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { colaboradoresService } from '../services/colaboradores.service';
import { formacoesAcademicasService } from '../services/formacoes-academicas.service';
import Tabs, { type Tab } from '../components/Tabs';
import { formatCPF, formatPhone } from '../utils/formatters';
import { ToastContainer, useToast } from '../components/Toast';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import PageContainer from '../components/PageContainer';
import PageHeader from '../components/PageHeader';

// Componentes das abas
import DadosPessoaisTab from '../components/ColaboradorFicha/DadosPessoaisTab';
import ExamesAgendamentosTab from '../components/ColaboradorFicha/ExamesAgendamentosTab';
import ResultadosLaudosTab from '../components/ColaboradorFicha/ResultadosLaudosTab';
import HistoricoTab from '../components/ColaboradorFicha/HistoricoTab';
import ColaboradorEpisTab from '../components/ColaboradorFicha/ColaboradorEpisTab';
import ColaboradorAtestadosTab from '../components/ColaboradorFicha/ColaboradorAtestadosTab';

export default function ColaboradorFicha() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTabState, setActiveTabState] = useState('dados-pessoais');
  const activeTab = searchParams.get('aba') || activeTabState;
  const { hasFeature, hasModulo } = useFeatureAccess();

  const handleTabChange = (tabId: string) => {
    setActiveTabState(tabId);
    setSearchParams({ aba: tabId });
  };
  const toast = useToast();

  const colaboradorId = id ? parseInt(id) : 0;

  // Buscar dados do colaborador
  const { data: colaborador, isLoading } = useQuery({
    queryKey: ['colaborador', colaboradorId],
    queryFn: () => colaboradoresService.findOne(colaboradorId),
    enabled: !!colaboradorId,
  });

  // Buscar formações acadêmicas
  const { data: formacoes = [] } = useQuery({
    queryKey: ['formacoesAcademicas'],
    queryFn: () => formacoesAcademicasService.findAll(),
  });

  if (isLoading) {
    return (
      <PageContainer>
        <div className="text-center py-8">
          <div className="text-gray-400">Carregando...</div>
        </div>
      </PageContainer>
    );
  }

  if (!colaborador) {
    return (
      <PageContainer>
        <div className="text-center py-8">
          <div className="text-gray-400">Colaborador não encontrado</div>
          <button
            onClick={() => navigate('/colaboradores')}
            className="mt-4 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors"
          >
            Voltar
          </button>
        </div>
      </PageContainer>
    );
  }

  const tabs: Tab[] = [
    {
      id: 'dados-pessoais',
      label: 'Dados Pessoais',
      content: <DadosPessoaisTab colaborador={colaborador} formacoes={formacoes} />,
    },
    {
      id: 'exames-agendamentos',
      label: 'Exames e Agendamentos',
      content: <ExamesAgendamentosTab colaboradorId={colaboradorId} />,
    },
    {
      id: 'resultados-laudos',
      label: 'Resultados e Laudos',
      content: <ResultadosLaudosTab colaboradorId={colaboradorId} />,
    },
    {
      id: 'historico',
      label: 'Histórico',
      content: <HistoricoTab colaboradorId={colaboradorId} />,
    },
    ...(hasModulo('saude_corporativa') || hasModulo('gestao_atestados') || hasFeature('atestados')
      ? [
          {
            id: 'atestados',
            label: 'Atestados & Saúde',
            content: (
              <ColaboradorAtestadosTab
                colaboradorId={colaboradorId}
                empresaId={colaborador.empresa_id || 0}
                colaboradorNome={colaborador.nome_social || colaborador.nome}
              />
            ),
          },
        ]
      : []),
    ...(hasFeature('epis')
      ? [
          {
            id: 'epis',
            label: 'EPIs e Equipamentos',
            content: <ColaboradorEpisTab colaboradorId={colaboradorId} />,
          },
        ]
      : []),
  ];

  return (
    <PageContainer>
      <PageHeader
        title={
          <div className="flex items-center gap-2 flex-wrap">
            <span>{colaborador.nome_social || colaborador.nome}</span>
            {colaborador.pcd && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                PCD
              </span>
            )}
            {colaborador.enviar_esocial === false && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                Sem eSocial
              </span>
            )}
          </div>
        }
        description={
          <div className="space-y-1">
            {colaborador.nome_social && colaborador.nome_social !== colaborador.nome && (
              <div className="text-xs text-gray-500 font-medium">
                Nome civil: {colaborador.nome}
              </div>
            )}
            <div className="text-sm text-gray-600 flex flex-wrap gap-4">
              <span>CPF: {formatCPF(colaborador.cpf)}</span>
              {colaborador.email && <span>Email: {colaborador.email}</span>}
              {colaborador.telefone && <span>Tel: {formatPhone(colaborador.telefone)}</span>}
            </div>
            {colaborador.cargo && colaborador.setor && (
              <div className="text-sm text-gray-500">
                <span>{colaborador.cargo.nome}</span>
                <span className="mx-2">•</span>
                <span>{colaborador.setor.nome}</span>
              </div>
            )}
          </div>
        }
        breadcrumbs={[
          { label: 'Colaboradores', href: '/colaboradores' },
          { label: colaborador.nome_social || colaborador.nome },
        ]}
      />

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow p-6">
            <Tabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
          </div>
      {/* Toast Container para notificações */}
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </PageContainer>
  );
}


